import { urlCategoryList, urlEditCategory, urlProductList } from '@/urls';
import { useCurrentClub } from '@/hooks/use-current-club';
import { LoaderFunctionArgs, useLoaderData, useNavigate, useRevalidator } from 'react-router';
import { getPagination, productApi } from '@repo/api-client';
import { ProductCategory } from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import { PencilIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { useAsync } from '@repo/ui/hooks/use-async';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/components/dialog';
import { Image } from '@repo/ui/components/image';
import { CreateCategoryForm } from '@/components/product/create-category-form';
import { useState } from 'react';
import { DataTable } from '@repo/ui/components/data-table';
import { DataTableRowActions } from '@repo/ui/components/data-table-row-actions';

function CategoryList() {
  const navigate = useNavigate();
  const club = useCurrentClub();
  const categories = useLoaderData<typeof route.loader>();
  const revalidator = useRevalidator();
  const [open, setOpen] = useState(false);

  const { execute: deleteCategory } = useAsync(async (categoryId: string) => {
    await productApi.deleteCategory(club.id, categoryId);
    revalidator.revalidate();
  });

  const columns = [
    {
      header: 'Imagen',
      accessorKey: 'imageUrl',
      cell: ({ row }: { row: { original: ProductCategory } }) => {
        return (
          <Image
            src={row.original.imageUrl}
            alt={row.original.name}
            className="w-10 h-10 border border-gray-300 rounded-full"
          />
        );
      },
    },
    {
      header: 'Nombre',
      accessorKey: 'name',
    },
    {
      header: 'Descripción',
      accessorKey: 'description',
    },
    {
      header: 'Acciones',
      accessorKey: 'actions',
      cell: ({ row }: { row: { original: ProductCategory } }) => {
        return (
          <DataTableRowActions
            actions={[
              {
                label: 'Editar',
                onClick: () => navigate(urlEditCategory(club.id, row.original.id)),
                icon: <PencilIcon className="w-4 h-4" />,
              },
              {
                label: 'Eliminar',
                onClick: () => deleteCategory(row.original.id),
                icon: <TrashIcon className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={categories.results}
      total={categories.count}
      mainActions={() => (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger>
            <Button>
              <PlusIcon className="w-4 h-4" />
              Crear Categoría
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogTitle>Crear Categoría</DialogTitle>
            <DialogDescription>
              Completa los campos para crear una nueva categoría.
            </DialogDescription>
            <CreateCategoryForm onClose={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      )}
    />
  );
}

export const route = {
  path: urlCategoryList(':id'),
  element: <CategoryList />,
  handle: {
    title: 'Categorías',
    backLink: (params: { id: string }) => urlProductList(params.id),
  },
  loader: async ({ params, request }: LoaderFunctionArgs) => {
    const pagination = getPagination(request);
    return await productApi.getCategories(params.id as string, pagination);
  },
};
